import { useState } from "react";
import { Table, Tag, Button, Modal, Form, Input, message } from "antd";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { CheckCircle2, XCircle, ClipboardList } from "lucide-react";
import { IWorkerQuoteRequest } from "../../../types/api/payslips";
import { useWorkerQuoteRequestList, useWorkerQuoteReview } from "../../../hooks/api/payslips/hooks";
import { SEMANTIC_HEX } from "../../../constants/statusColors";

const STATUS_COLOR: Record<string, string> = { pending: "gold", approved: "green", rejected: "red" };

function ReviewModal({
  record,
  action,
  onClose,
}: {
  record: IWorkerQuoteRequest | null;
  action: "approved" | "rejected" | null;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();

  const review = useWorkerQuoteReview({
    onSuccess: () => { msgApi.success(`Quote request ${action}.`); onClose(); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  if (!record || !action) return null;

  return (
    <Modal
      open={!!record}
      onCancel={() => { onClose(); form.resetFields(); }}
      title={action === "approved" ? "Approve Quote Request" : "Reject Quote Request"}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button
          key="confirm"
          type="primary"
          danger={action === "rejected"}
          loading={review.isPending}
          onClick={() => form.submit()}
          style={action === "approved" ? { background: SEMANTIC_HEX.success, border: "none" } : undefined}
        >
          {action === "approved" ? "Approve" : "Reject"}
        </Button>,
      ]}
    >
      {ctx}
      <div className="mb-4 bg-slate-50 border border-slate-100 rounded-lg p-3 text-sm">
        <div className="font-medium text-slate-700 mb-1">{record.description}</div>
        <div className="text-xs text-slate-400">Job {record.job_number || "—"} · Submitted by {record.submitted_by_name || "—"}</div>
        {record.estimated_amount && (
          <div className="text-sm font-semibold text-slate-800 mt-1">Est. £{Number(record.estimated_amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</div>
        )}
      </div>
      <Form form={form} layout="vertical" size="middle" onFinish={(vals) => review.mutate({ id: record.id, status: action, review_notes: vals.review_notes })}>
        <Form.Item name="review_notes" label={action === "rejected" ? "Reason for rejection" : "Notes (optional)"}>
          <Input.TextArea rows={3} placeholder={action === "rejected" ? "Explain why this is being rejected…" : "Optional approval notes…"} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const WorkerQuotesPage = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [reviewRecord, setReviewRecord] = useState<IWorkerQuoteRequest | null>(null);
  const [reviewAction, setReviewAction] = useState<"approved" | "rejected" | null>(null);

  const qData = { page: 1, limit: 20, ...(statusFilter && { status: statusFilter }) };
  const { data: res, isLoading, refetchWithQuery } = useWorkerQuoteRequestList(qData);
  const requests: IWorkerQuoteRequest[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);
  const pendingCount = requests.filter((r) => r.status === "pending").length;

  const columns: ColumnType<IWorkerQuoteRequest>[] = [
    {
      title: "Submitted",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => <span className="text-xs text-slate-500">{dayjs(v).format("DD MMM YYYY HH:mm")}</span>,
      width: 130,
    },
    {
      title: "Job",
      key: "job",
      render: (_, r) => (
        <div>
          <div className="text-sm font-medium text-slate-800">{r.job_number ? `Job ${r.job_number}` : "—"}</div>
          {r.job_title && <div className="text-xs text-slate-400 truncate max-w-32">{r.job_title}</div>}
        </div>
      ),
      width: 140,
    },
    {
      title: "Worker",
      dataIndex: "submitted_by_name",
      key: "submitted_by_name",
      render: (v) => <span className="text-sm text-slate-700">{v || "—"}</span>,
      width: 140,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (v) => <span className="text-sm text-slate-600 line-clamp-2">{v}</span>,
    },
    {
      title: "Est. Amount",
      dataIndex: "estimated_amount",
      key: "estimated_amount",
      render: (v) => v ? <span className="font-semibold text-slate-800">£{Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span> : <span className="text-slate-300 text-xs">—</span>,
      width: 110,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
      width: 90,
    },
    {
      title: "",
      key: "actions",
      width: 130,
      render: (_, r) => r.status === "pending" ? (
        <div className="flex gap-1.5">
          <Button size="small" icon={<CheckCircle2 size={11} />} style={{ color: SEMANTIC_HEX.success, borderColor: SEMANTIC_HEX.success, background: "transparent" }}
            onClick={() => { setReviewRecord(r); setReviewAction("approved"); }}>
            Approve
          </Button>
          <Button size="small" danger icon={<XCircle size={11} />}
            onClick={() => { setReviewRecord(r); setReviewAction("rejected"); }}>
            Reject
          </Button>
        </div>
      ) : (
        <div className="text-xs text-slate-400">
          {r.reviewed_by_name && <div>{r.reviewed_by_name}</div>}
          {r.reviewed_at && <div>{dayjs(r.reviewed_at).format("DD MMM")}</div>}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-800">Worker Quote Requests</h1>
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingCount} pending</span>
            )}
          </div>
          <p className="text-slate-500 text-sm">{total} requests · Field worker additional cost approvals</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {(["all", "pending", "approved", "rejected"] as const).map((s) => (
          <button
            key={s}
            onClick={() => { const v = s === "all" ? undefined : s; setStatusFilter(v); refetchWithQuery({ ...qData, status: v }); }}
            className={`px-3 py-1 text-xs rounded-full border font-medium transition-colors ${
              (statusFilter ?? "all") === s ? "!bg-primary text-white !border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={requests}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, total, showTotal: (t) => `${t} requests`, showSizeChanger: false }}
          scroll={{ x: 900 }}
          rowClassName={(r) => r.status === "pending" ? "bg-amber-50/30" : ""}
        />
      </div>

      <ReviewModal
        record={reviewRecord}
        action={reviewAction}
        onClose={() => { setReviewRecord(null); setReviewAction(null); }}
      />
    </div>
  );
};

export default WorkerQuotesPage;
